import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Facebook, Linkedin, Wifi, WifiOff, Clock, CheckCircle, AlertCircle,
  FileEdit, Send, ThumbsUp, MessageSquare, X, Pencil, Trash2, TrendingUp,
  DollarSign, MousePointerClick, Eye, Target, BarChart3, PlayCircle, PauseCircle,
  CheckCheck, Chrome, Image as ImageIcon, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = ['Google Ads', 'Meta Ads', 'ChatGPT (OpenAI) Ads', 'LinkedIn Ads', 'Microsoft Ads'];
const OBJECTIVES = ['Leads', 'Traffic', 'Awareness', 'Conversions'];
const CAMPAIGN_STATUS = ['draft', 'pending_approval', 'approved', 'live', 'paused', 'completed', 'changes_requested'];
const CREATIVE_STATUS = ['draft', 'pending_approval', 'approved', 'live', 'changes_requested'];

const platformIcon = (p) => {
  if (p === 'Google Ads') return Chrome;
  if (p === 'Meta Ads') return Facebook;
  if (p === 'LinkedIn Ads') return Linkedin;
  return Target; // Microsoft Ads
};

const platformColor = (p) => {
  if (p === 'Google Ads') return 'from-yellow-400 via-red-500 to-blue-500';
  if (p === 'Meta Ads') return 'from-blue-500 to-purple-600';
  if (p === 'LinkedIn Ads') return 'from-sky-600 to-blue-700';
  return 'from-slate-600 to-slate-800';
};

const statusConfig = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600', icon: FileEdit },
  pending_approval: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  live: { label: 'Live', cls: 'bg-green-100 text-green-700', icon: PlayCircle },
  paused: { label: 'Paused', cls: 'bg-orange-100 text-orange-700', icon: PauseCircle },
  completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700', icon: CheckCheck },
  changes_requested: { label: 'Changes Requested', cls: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const money = (n) => `£${Number(n || 0).toLocaleString()}`;

// ────────────────────────────────────────────────────────────────
// Feedback Modal (reused for client "Suggest Changes")
// ────────────────────────────────────────────────────────────────
function FeedbackModal({ onClose, onSubmit }) {
  const [text, setText] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="ads-feedback-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Suggest Changes</h3>
        <p className="text-sm text-slate-500 mb-4">Tell the team what you'd like changed before approving.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none mb-4"
          placeholder="Describe the changes you'd like..." data-testid="ads-feedback-textarea" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => { if (text.trim()) onSubmit(text); }} data-testid="ads-feedback-submit-btn"
            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:shadow-lg">
            Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 1) Account Access
// ────────────────────────────────────────────────────────────────
function AccountAccessTab({ firmId, user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isClient = user.role === 'client';

  useEffect(() => { fetchAccounts(); /* eslint-disable-next-line */ }, [firmId]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/ads/accounts/${firmId}`, { headers: authHeaders() });
      setAccounts(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/ads/accounts/${id}`, { status }, { headers: authHeaders() });
      fetchAccounts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const statusLabel = { not_connected: 'Not Connected', pending_access: 'Pending Access', connected: 'Connected' };
  const statusCls = { not_connected: 'bg-slate-100 text-slate-600', pending_access: 'bg-amber-100 text-amber-700', connected: 'bg-emerald-100 text-emerald-700' };
  const statusIco = { not_connected: WifiOff, pending_access: Clock, connected: Wifi };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading ad accounts...</div>;

  return (
    <div className="space-y-6" data-testid="ads-account-access">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Connect Your Ad Accounts</h2>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          To manage your paid advertising, please grant access to your ad accounts using the email below. Once access is granted, our team will update the status here.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 rounded-xl">
          <span className="text-sm font-medium text-violet-700">ads@yourdomain.com</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map((acc) => {
          const Icon = platformIcon(acc.platform);
          const SIcon = statusIco[acc.status] || WifiOff;
          const slug = acc.platform.toLowerCase().replace(/[^a-z]/g, '');
          return (
            <div key={acc.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`ads-account-${slug}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${platformColor(acc.platform)} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{acc.platform}</div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${statusCls[acc.status]}`}>
                      <SIcon className="w-3 h-3" />{statusLabel[acc.status]}
                    </span>
                  </div>
                </div>
                {!isClient && (
                  <select value={acc.status} onChange={(e) => updateStatus(acc.id, e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                    data-testid={`ads-account-status-${slug}`}>
                    <option value="not_connected">Not Connected</option>
                    <option value="pending_access">Pending Access</option>
                    <option value="connected">Connected</option>
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 2) Campaign Form Modal
// ────────────────────────────────────────────────────────────────
function CampaignFormModal({ campaign, firmId, onClose, onSaved }) {
  const [form, setForm] = useState(campaign || {
    name: '', platform: 'Google Ads', objective: 'Leads', monthly_budget: 0,
    start_date: '', end_date: '', target_location: '', notes: '', status: 'draft'
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Campaign name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, monthly_budget: Number(form.monthly_budget || 0) };
      if (campaign?.id) {
        await axios.put(`${API}/ads/campaigns/${campaign.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/ads/campaigns/${firmId}`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error saving'); }
    finally { setSaving(false); }
  };

  const field = 'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="campaign-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{campaign?.id ? 'Edit Campaign' : 'Add Campaign'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className={field} placeholder="e.g. Personal Injury Leads" data-testid="campaign-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
              <select value={form.platform} onChange={(e) => setForm(f => ({ ...f, platform: e.target.value }))} className={field} data-testid="campaign-platform-select">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Objective</label>
              <select value={form.objective} onChange={(e) => setForm(f => ({ ...f, objective: e.target.value }))} className={field} data-testid="campaign-objective-select">
                {OBJECTIVES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Budget (£)</label>
              <input type="number" min="0" value={form.monthly_budget} onChange={(e) => setForm(f => ({ ...f, monthly_budget: e.target.value }))} className={field} data-testid="campaign-budget-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Target Location</label>
              <input value={form.target_location} onChange={(e) => setForm(f => ({ ...f, target_location: e.target.value }))} className={field} placeholder="e.g. London, UK" data-testid="campaign-location-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <input type="date" value={form.start_date || ''} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className={field} data-testid="campaign-start-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
              <input type="date" value={form.end_date || ''} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className={field} data-testid="campaign-end-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className={field + ' resize-none'} placeholder="Internal notes..." data-testid="campaign-notes-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className={field} data-testid="campaign-status-select">
              {CAMPAIGN_STATUS.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="campaign-save-btn">
              {saving ? 'Saving...' : (campaign?.id ? 'Update Campaign' : 'Create Campaign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 3) Campaigns Tab
// ────────────────────────────────────────────────────────────────
function CampaignsTab({ firmId, user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const isClient = user.role === 'client';

  useEffect(() => { fetchCampaigns(); /* eslint-disable-next-line */ }, [firmId]);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/ads/campaigns/${firmId}`, { headers: authHeaders() });
      setCampaigns(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const seed = async () => {
    try { await axios.post(`${API}/ads/seed/${firmId}`, {}, { headers: authHeaders() }); fetchCampaigns(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this campaign? Linked creatives and performance will be removed.')) return;
    try { await axios.delete(`${API}/ads/campaigns/${id}`, { headers: authHeaders() }); fetchCampaigns(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const updateStatus = async (id, status, feedback) => {
    try {
      const body = { status };
      if (feedback !== undefined) body.feedback = feedback;
      await axios.put(`${API}/ads/campaigns/${id}`, body, { headers: authHeaders() });
      fetchCampaigns();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading campaigns...</div>;

  return (
    <div className="space-y-6" data-testid="ads-campaigns">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Campaigns</h2>
        {!isClient && (
          <div className="flex items-center gap-3">
            {campaigns.length === 0 && (
              <button onClick={seed} className="px-4 py-2 text-sm text-violet-600 border border-violet-200 rounded-xl hover:bg-violet-50 font-medium transition-colors" data-testid="ads-seed-btn">
                Load Sample Data
              </button>
            )}
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30" data-testid="add-campaign-btn">
              <Plus className="w-4 h-4" />Add Campaign
            </button>
          </div>
        )}
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No campaigns yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first paid campaign</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaigns.map((c) => {
            const Icon = platformIcon(c.platform);
            const s = statusConfig[c.status] || statusConfig.draft;
            const SIcon = s.icon;
            return (
              <div key={c.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`campaign-card-${c.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${platformColor(c.platform)} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`} data-testid={`campaign-status-${c.id}`}>
                          <SIcon className="w-3 h-3" />{s.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{c.platform} · {c.objective}</div>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-3 text-xs">
                        <span className="flex items-center gap-1 text-slate-600"><DollarSign className="w-3 h-3" />{money(c.monthly_budget)}/mo</span>
                        {c.start_date && <span className="text-slate-400">{c.start_date} → {c.end_date || '-'}</span>}
                        {c.target_location && <span className="text-slate-400">📍 {c.target_location}</span>}
                      </div>
                      {c.feedback && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800" data-testid={`campaign-feedback-${c.id}`}>
                          <span className="font-medium">Feedback:</span> {c.feedback}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                  {!isClient && c.status === 'draft' && (
                    <button onClick={() => updateStatus(c.id, 'pending_approval')} className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg" data-testid={`campaign-notify-${c.id}`}>
                      Notify for Approval
                    </button>
                  )}
                  {!isClient && (
                    <>
                      <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`campaign-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(c.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                  {isClient && c.status === 'pending_approval' && (
                    <>
                      <button onClick={() => updateStatus(c.id, 'approved')} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl" data-testid={`campaign-approve-${c.id}`}>
                        <ThumbsUp className="w-3.5 h-3.5" />Approve
                      </button>
                      <button onClick={() => setFeedbackFor(c.id)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl" data-testid={`campaign-suggest-${c.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" />Suggest Changes
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <CampaignFormModal campaign={editing} firmId={firmId} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); fetchCampaigns(); }} />}
      {feedbackFor && <FeedbackModal onClose={() => setFeedbackFor(null)} onSubmit={(text) => { updateStatus(feedbackFor, 'changes_requested', text); setFeedbackFor(null); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 4) Creative Form Modal
// ────────────────────────────────────────────────────────────────
function CreativeFormModal({ creative, campaigns, firmId, onClose, onSaved }) {
  const [form, setForm] = useState(creative || {
    campaign_id: campaigns[0]?.id || '', platform: campaigns[0]?.platform || 'Google Ads',
    headline: '', primary_text: '', cta: 'Learn More', media_url: '', landing_page_url: '', status: 'draft'
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.headline.trim() || !form.primary_text.trim()) { alert('Headline and primary text required'); return; }
    setSaving(true);
    try {
      const chosen = campaigns.find(c => c.id === form.campaign_id);
      const payload = { ...form, campaign_name: chosen?.name || form.campaign_name || '' };
      if (creative?.id) {
        await axios.put(`${API}/ads/creatives/${creative.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/ads/creatives/${firmId}`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const field = 'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="creative-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{creative?.id ? 'Edit Ad Creative' : 'Add Ad Creative'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Campaign</label>
            <select value={form.campaign_id} onChange={(e) => {
              const chosen = campaigns.find(c => c.id === e.target.value);
              setForm(f => ({ ...f, campaign_id: e.target.value, platform: chosen?.platform || f.platform }));
            }} className={field} data-testid="creative-campaign-select">
              <option value="">- Standalone -</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name} ({c.platform})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
            <select value={form.platform} onChange={(e) => setForm(f => ({ ...f, platform: e.target.value }))} className={field} data-testid="creative-platform-select">
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Headline</label>
            <input value={form.headline} onChange={(e) => setForm(f => ({ ...f, headline: e.target.value }))} className={field} placeholder="Grab attention in 1 line" data-testid="creative-headline-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Primary Text</label>
            <textarea rows={3} value={form.primary_text} onChange={(e) => setForm(f => ({ ...f, primary_text: e.target.value }))} className={field + ' resize-none'} placeholder="Longer description..." data-testid="creative-text-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Call to Action</label>
              <input value={form.cta} onChange={(e) => setForm(f => ({ ...f, cta: e.target.value }))} className={field} placeholder="Contact Us" data-testid="creative-cta-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Landing Page URL</label>
              <input value={form.landing_page_url || ''} onChange={(e) => setForm(f => ({ ...f, landing_page_url: e.target.value }))} className={field} placeholder="https://..." data-testid="creative-url-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Media URL (image or video)</label>
            <input value={form.media_url || ''} onChange={(e) => setForm(f => ({ ...f, media_url: e.target.value }))} className={field} placeholder="https://..." data-testid="creative-media-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className={field} data-testid="creative-status-select">
              {CREATIVE_STATUS.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="creative-save-btn">
              {saving ? 'Saving...' : (creative?.id ? 'Update Creative' : 'Create Creative')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 5) Ad Creatives Tab
// ────────────────────────────────────────────────────────────────
function CreativesTab({ firmId, user }) {
  const [creatives, setCreatives] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const isClient = user.role === 'client';

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [firmId]);

  const fetchAll = async () => {
    try {
      const [cr, ca] = await Promise.all([
        axios.get(`${API}/ads/creatives/${firmId}`, { headers: authHeaders() }),
        axios.get(`${API}/ads/campaigns/${firmId}`, { headers: authHeaders() }),
      ]);
      setCreatives(cr.data); setCampaigns(ca.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this ad creative?')) return;
    try { await axios.delete(`${API}/ads/creatives/${id}`, { headers: authHeaders() }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const updateStatus = async (id, status, feedback) => {
    try {
      const body = { status };
      if (feedback !== undefined) body.feedback = feedback;
      await axios.put(`${API}/ads/creatives/${id}`, body, { headers: authHeaders() });
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading creatives...</div>;

  return (
    <div className="space-y-6" data-testid="ads-creatives">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Ad Creatives</h2>
        {!isClient && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30" data-testid="add-creative-btn">
            <Plus className="w-4 h-4" />Add Ad Creative
          </button>
        )}
      </div>

      {creatives.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No creatives yet</p>
          <p className="text-slate-400 text-sm mt-1">Add your first ad creative</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {creatives.map((cr) => {
            const Icon = platformIcon(cr.platform);
            const s = statusConfig[cr.status] || statusConfig.draft;
            const SIcon = s.icon;
            return (
              <div key={cr.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`creative-card-${cr.id}`}>
                <div className="h-32 bg-gradient-to-br from-slate-100 to-violet-100/50 flex items-center justify-center relative">
                  {cr.media_url ? (
                    <img src={cr.media_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-slate-300" />
                  )}
                  <div className={`absolute top-3 left-3 w-8 h-8 rounded-lg bg-gradient-to-br ${platformColor(cr.platform)} flex items-center justify-center shadow-md`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className={`absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`} data-testid={`creative-status-${cr.id}`}>
                    <SIcon className="w-3 h-3" />{s.label}
                  </span>
                </div>
                <div className="p-4">
                  {cr.campaign_name && <div className="text-xs text-slate-400 mb-1 truncate">↳ {cr.campaign_name}</div>}
                  <h3 className="font-semibold text-slate-900 line-clamp-2">{cr.headline}</h3>
                  <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{cr.primary_text}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-violet-50 text-violet-600 rounded-lg font-medium">{cr.cta}</span>
                      {cr.landing_page_url && <a href={cr.landing_page_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-violet-600"><ExternalLink className="w-3.5 h-3.5" /></a>}
                    </div>
                    <div className="flex items-center gap-1">
                      {!isClient && cr.status === 'draft' && (
                        <button onClick={() => updateStatus(cr.id, 'pending_approval')} className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg" data-testid={`creative-notify-${cr.id}`}>Notify</button>
                      )}
                      {!isClient && (
                        <>
                          <button onClick={() => { setEditing(cr); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`creative-edit-${cr.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => del(cr.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`creative-delete-${cr.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      {isClient && cr.status === 'pending_approval' && (
                        <>
                          <button onClick={() => updateStatus(cr.id, 'approved')} className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg" data-testid={`creative-approve-${cr.id}`}>Approve</button>
                          <button onClick={() => setFeedbackFor(cr.id)} className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg" data-testid={`creative-suggest-${cr.id}`}>Suggest</button>
                        </>
                      )}
                    </div>
                  </div>
                  {cr.feedback && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid={`creative-feedback-${cr.id}`}>
                      <span className="font-medium">Feedback:</span> {cr.feedback}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <CreativeFormModal creative={editing} campaigns={campaigns} firmId={firmId} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }} />}
      {feedbackFor && <FeedbackModal onClose={() => setFeedbackFor(null)} onSubmit={(text) => { updateStatus(feedbackFor, 'changes_requested', text); setFeedbackFor(null); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// 6) Performance Tab
// ────────────────────────────────────────────────────────────────
function PerformanceFormModal({ record, campaigns, firmId, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState(record || {
    campaign_id: campaigns[0]?.id || '',
    period_start: monthAgo, period_end: today,
    spend: 0, impressions: 0, clicks: 0, leads: 0, notes: ''
  });
  const [saving, setSaving] = useState(false);

  const impressions = Number(form.impressions) || 0;
  const clicks = Number(form.clicks) || 0;
  const spend = Number(form.spend) || 0;
  const leads = Number(form.leads) || 0;
  const ctr = impressions ? ((clicks / impressions) * 100).toFixed(2) : 0;
  const cpl = leads ? (spend / leads).toFixed(2) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.campaign_id) { alert('Please select a campaign'); return; }
    setSaving(true);
    try {
      const payload = {
        campaign_id: form.campaign_id,
        period_start: form.period_start, period_end: form.period_end,
        spend: Number(form.spend), impressions: Number(form.impressions),
        clicks: Number(form.clicks), leads: Number(form.leads), notes: form.notes,
      };
      if (record?.id) {
        await axios.put(`${API}/ads/performance/${record.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/ads/performance/${firmId}`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error saving'); }
    finally { setSaving(false); }
  };

  const field = 'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="performance-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{record?.id ? 'Edit Performance Update' : 'Add Performance Update'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Campaign</label>
            <select value={form.campaign_id} onChange={(e) => setForm(f => ({ ...f, campaign_id: e.target.value }))} className={field} data-testid="perf-campaign-select">
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name} ({c.platform})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reporting Period Start</label>
              <input type="date" value={form.period_start || ''} onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))} className={field} data-testid="perf-period-start" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reporting Period End</label>
              <input type="date" value={form.period_end || ''} onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))} className={field} data-testid="perf-period-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Spend (£)</label>
              <input type="number" min="0" step="0.01" value={form.spend} onChange={(e) => setForm(f => ({ ...f, spend: e.target.value }))} className={field} data-testid="perf-spend-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Impressions</label>
              <input type="number" min="0" value={form.impressions} onChange={(e) => setForm(f => ({ ...f, impressions: e.target.value }))} className={field} data-testid="perf-impressions-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Clicks</label>
              <input type="number" min="0" value={form.clicks} onChange={(e) => setForm(f => ({ ...f, clicks: e.target.value }))} className={field} data-testid="perf-clicks-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Leads</label>
              <input type="number" min="0" value={form.leads} onChange={(e) => setForm(f => ({ ...f, leads: e.target.value }))} className={field} data-testid="perf-leads-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 p-3 bg-violet-50 border border-violet-100 rounded-xl text-sm">
            <div><span className="text-slate-500">CTR (auto):</span> <span className="font-semibold text-violet-700" data-testid="perf-ctr-computed">{ctr}%</span></div>
            <div><span className="text-slate-500">Cost per Lead (auto):</span> <span className="font-semibold text-violet-700" data-testid="perf-cpl-computed">{leads ? money(cpl) : '-'}</span></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className={field + ' resize-none'} placeholder="Observations, learnings, next actions..." data-testid="perf-notes-input" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="perf-save-btn">
              {saving ? 'Saving...' : (record?.id ? 'Update Update' : 'Add Performance Update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PerformanceTab({ firmId, user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const isClient = user.role === 'client';

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [firmId]);

  const fetchAll = async () => {
    try {
      const [ca, pe] = await Promise.all([
        axios.get(`${API}/ads/campaigns/${firmId}`, { headers: authHeaders() }),
        axios.get(`${API}/ads/performance/${firmId}`, { headers: authHeaders() }),
      ]);
      setCampaigns(ca.data); setPerformance(pe.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this performance update?')) return;
    try { await axios.delete(`${API}/ads/performance/${id}`, { headers: authHeaders() }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const campaignName = (id) => campaigns.find(c => c.id === id)?.name || '-';

  const totals = useMemo(() => {
    const t = { spend: 0, impressions: 0, clicks: 0, leads: 0 };
    performance.forEach(p => {
      t.spend += Number(p.spend || 0);
      t.impressions += Number(p.impressions || 0);
      t.clicks += Number(p.clicks || 0);
      t.leads += Number(p.leads || 0);
    });
    t.ctr = t.impressions ? ((t.clicks / t.impressions) * 100).toFixed(2) : 0;
    t.cpl = t.leads ? (t.spend / t.leads).toFixed(2) : 0;
    return t;
  }, [performance]);

  if (loading) return <div className="text-center py-12 text-slate-500">Loading performance...</div>;

  return (
    <div className="space-y-6" data-testid="ads-performance">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Performance</h2>
          <p className="text-slate-500 text-sm mt-1">Manual performance tracking across all active campaigns.</p>
        </div>
        {!isClient && campaigns.length > 0 && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30"
            data-testid="add-performance-btn">
            <Plus className="w-4 h-4" />Add Performance Update
          </button>
        )}
      </div>

      {/* Summary KPI Cards (aggregated across all records) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Spend', value: money(totals.spend), icon: DollarSign, grad: 'from-violet-500 to-indigo-600' },
          { label: 'Impressions', value: totals.impressions.toLocaleString(), icon: Eye, grad: 'from-blue-500 to-cyan-500' },
          { label: 'Clicks', value: totals.clicks.toLocaleString(), icon: MousePointerClick, grad: 'from-fuchsia-500 to-pink-500' },
          { label: 'Leads', value: totals.leads, icon: TrendingUp, grad: 'from-emerald-500 to-teal-500' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm" data-testid={`perf-kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-500 uppercase">{kpi.label}</span>
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${kpi.grad} flex items-center justify-center`}>
                <kpi.icon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm">
          <div className="text-xs font-medium text-slate-500 uppercase">Avg CTR</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>{totals.ctr}%</div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 shadow-sm">
          <div className="text-xs font-medium text-slate-500 uppercase">Avg Cost per Lead</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>{totals.leads ? money(totals.cpl) : '-'}</div>
        </div>
      </div>

      {/* Performance records table */}
      {campaigns.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No campaigns to track</p>
          <p className="text-slate-400 text-sm mt-1">Create a campaign first.</p>
        </div>
      ) : performance.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No performance updates yet</p>
          {!isClient && <p className="text-slate-400 text-sm mt-1">Click "Add Performance Update" to log the first reporting period.</p>}
        </div>
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm overflow-x-auto">
          <table className="w-full" data-testid="perf-table">
            <thead className="bg-slate-50/80 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Spend</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Impressions</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Clicks</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">CTR</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Leads</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">CPL</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase max-w-[240px]">Notes</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {performance.map(p => (
                <tr key={p.id} className="hover:bg-violet-50/30 transition-colors" data-testid={`perf-row-${p.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{campaignName(p.campaign_id)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{p.period_start || '-'} → {p.period_end || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{money(p.spend)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{(p.impressions || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{(p.clicks || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{p.ctr ? `${p.ctr}%` : '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{p.leads || 0}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{p.cpl ? money(p.cpl) : '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[240px]">
                    <div className="truncate" title={p.notes}>{p.notes || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    {!isClient && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`perf-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(p.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`perf-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <PerformanceFormModal record={editing} campaigns={campaigns} firmId={firmId} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); fetchAll(); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Main export
// ────────────────────────────────────────────────────────────────
import PaidAdsPaywallModal from './PaidAdsPaywallModal';
import AddonEntitlements from './AddonEntitlements';

export default function PaidAds({ user, selectedClient }) {
  const [activeTab, setActiveTab] = useState('campaigns');
  const firmId = selectedClient?.firm_id || selectedClient?.firmId || user.firm?.id || user.firm_id;
  const isClient = user.role === 'client';

  const [adsActive, setAdsActive] = useState(null); // null = loading
  const [paywallOpen, setPaywallOpen] = useState(false);

  const refreshAdsState = async () => {
    if (!firmId) return null;
    try {
      const res = await axios.get(`${API}/me/billing`, { headers: authHeaders() });
      const data = res.data || {};
      const ac = data.plan_config?.addons_config || {};
      const active = !!(data.ads_active || ac.paid_ads === 'enabled' || data.manual_override_enabled);
      setAdsActive(active);
      return active;
    } catch (_e) { setAdsActive(false); return false; }
  };

  useEffect(() => {
    if (!isClient) { setAdsActive(true); return; } // staff/admin always allowed
    refreshAdsState();
  }, [firmId, isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open paywall when client lands here without an active plan
  useEffect(() => {
    if (adsActive === false && isClient) setPaywallOpen(true);
    if (adsActive === true) setPaywallOpen(false);
  }, [adsActive, isClient]);

  // Post-Stripe-checkout polling - same pattern as Website Creation / Social Media
  useEffect(() => {
    if (!firmId || !isClient) return undefined;
    if (adsActive) return undefined;
    let stamp;
    try { stamp = localStorage.getItem('paid_ads_paying_at'); } catch (_e) { stamp = null; }
    if (!stamp) return undefined;
    const ageMs = Date.now() - new Date(stamp).getTime();
    if (Number.isNaN(ageMs) || ageMs > 1000 * 60 * 30) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const ok = await refreshAdsState();
      if (ok) {
        try { localStorage.removeItem('paid_ads_paying_at'); } catch (_e) { /* ignore */ }
        return;
      }
      if (attempts >= 20) return;
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [firmId, isClient, adsActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'account-access', label: 'Account Access' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'creatives', label: 'Ad Creatives' },
    { id: 'performance', label: 'Performance' },
  ];

  if (!firmId) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Select a client to manage paid ads.</p>
      </div>
    );
  }

  // While we resolve the addon state, show a tiny loader (no flash of unauth UI)
  if (adsActive === null) {
    return <div className="text-center py-16 text-slate-400" data-testid="paid-ads-loading">Loading paid ads…</div>;
  }

  const isLocked = adsActive === false && isClient;

  return (
    <div className="space-y-6 animate-fadeInUp relative" data-testid="paid-ads-module">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Paid Ads</h1>
        <p className="text-slate-500 mt-1">Manage campaigns, creatives, approvals, and performance.</p>
      </div>

      <AddonEntitlements firmId={firmId} addonKey="paid_ads" />

      {/* Underlying view stays visible-but-locked when no plan */}
      <div className={isLocked ? 'pointer-events-none opacity-50 select-none' : ''} aria-disabled={isLocked} data-testid="paid-ads-locked-overlay-target">
        <div className="flex items-center gap-1 bg-slate-100/80 rounded-xl p-1 overflow-x-auto" data-testid="ads-tabs">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => !isLocked && setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`ads-tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'account-access' && <AccountAccessTab firmId={firmId} user={user} />}
        {activeTab === 'campaigns' && <CampaignsTab firmId={firmId} user={user} />}
        {activeTab === 'creatives' && <CreativesTab firmId={firmId} user={user} />}
        {activeTab === 'performance' && <PerformanceTab firmId={firmId} user={user} />}
      </div>

      {/* Locked CTA banner so the user can re-open the paywall after dismissing */}
      {isLocked && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-5 py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 shadow-2xl shadow-violet-900/40 flex items-center gap-3" data-testid="paid-ads-locked-cta">
          <span className="text-white text-sm font-semibold">Activate Paid Ads to start campaigns</span>
          <button onClick={() => setPaywallOpen(true)} className="px-3 py-1.5 rounded-lg bg-white text-violet-700 text-xs font-bold hover:bg-violet-50 transition" data-testid="paid-ads-reopen-paywall">
            Choose plan
          </button>
        </div>
      )}

      <PaidAdsPaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
}
